// ============================================
// moldClaw Dashboard Page
// ============================================

/**
 * 🧑‍💻 SWE-1: 대시보드 UI
 * 🧑‍💻 SWE-3: 상태 관리
 * 🎨 CX-1: UX 리뷰 적용
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLicense, TIER_INFO } from '../hooks/useLicense';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    currentLicense,
    devices,
    isLoading,
    isDevicesLoading,
    error,
    loadDevices,
    removeDevice,
    manageSubscription,
  } = useLicense();
  
  const [removeLoading, setRemoveLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  
  // 기기 목록 로드
  useEffect(() => {
    if (currentLicense) {
      loadDevices();
    }
  }, [currentLicense, loadDevices]);
  
  const handleRemoveDevice = async (deviceId: string, deviceName: string) => {
    if (!confirm(`"${deviceName}" 기기를 제거하시겠습니까?\n이 기기에서 더 이상 moldClaw를 사용할 수 없게 됩니다.`)) {
      return;
    }
    
    setRemoveLoading(deviceId);
    setActionError(null);
    
    try {
      await removeDevice(deviceId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '기기 제거에 실패했습니다');
    } finally {
      setRemoveLoading(null);
    }
  };
  
  const handleManageSubscription = async () => {
    setPortalLoading(true);
    setActionError(null);
    
    try {
      await manageSubscription();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '구독 관리 페이지 접근에 실패했습니다');
      setPortalLoading(false);
    }
  };
  
  // 로딩 상태
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner className="w-12 h-12 text-space-blue mx-auto" />
          <p className="mt-4 text-gray-400">라이선스 정보 로딩 중...</p>
        </div>
      </div>
    );
  }
  
  const tierInfo = currentLicense ? TIER_INFO[currentLicense.tier] : null;
  
  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">대시보드</h1>
          <p className="mt-2 text-gray-400">
            안녕하세요, {user?.display_name || user?.email}님!
          </p>
        </div>
        
        {/* Error Messages */}
        {(error || actionError) && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400">
            {error || actionError}
          </div>
        )}
        
        {/* No License State */}
        {!currentLicense ? (
          <div className="bg-gray-800 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              라이선스가 없습니다
            </h2>
            <p className="text-gray-400 mb-6">
              플랜을 구독하여 moldClaw의 모든 기능을 사용해 보세요.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="px-6 py-3 bg-space-blue text-white rounded-lg font-semibold hover:bg-space-blue hover:brightness-110 transition-colors"
            >
              요금제 보기
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* License Info Card */}
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">라이선스 정보</h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  currentLicense.status === 'active' 
                    ? 'bg-green-500/20 text-green-400'
                    : currentLicense.status === 'suspended'
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {currentLicense.status === 'active' ? '활성' :
                   currentLicense.status === 'suspended' ? '일시정지' : '만료'}
                </span>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Plan Info */}
                <div>
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      currentLicense.tier === 'team' ? 'bg-purple-500/20' :
                      currentLicense.tier === 'pro' ? 'bg-space-blue/20' : 'bg-gray-700'
                    }`}>
                      <CrownIcon className={`w-6 h-6 ${
                        currentLicense.tier === 'team' ? 'text-purple-400' :
                        currentLicense.tier === 'pro' ? 'text-space-blue-light' : 'text-gray-400'
                      }`} />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white">
                        {tierInfo?.name} 플랜
                      </h3>
                      <p className="text-sm text-gray-400">{tierInfo?.description}</p>
                    </div>
                  </div>
                  
                  {/* License Code */}
                  <div className="bg-gray-900 rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-500 mb-1">라이선스 코드</p>
                    <code className="text-sm text-gray-300 font-mono">
                      {currentLicense.code}
                    </code>
                  </div>
                  
                  {/* Validity */}
                  {currentLicense.valid_until && (
                    <div className="text-sm text-gray-400">
                      <span>유효 기간: </span>
                      <span className="text-gray-300">
                        {new Date(currentLicense.valid_until).toLocaleDateString('ko-KR')}
                      </span>
                      까지
                    </div>
                  )}
                </div>
                
                {/* Device Usage */}
                <div>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">기기 사용량</span>
                      <span className="text-white font-medium">
                        {currentLicense.devices_used} / {currentLicense.max_devices}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          currentLicense.devices_used >= currentLicense.max_devices
                            ? 'bg-red-500'
                            : 'bg-space-blue'
                        }`}
                        style={{
                          width: `${(currentLicense.devices_used / currentLicense.max_devices) * 100}%`,
                        }}
                      />
                    </div>
                    {currentLicense.devices_used >= currentLicense.max_devices && (
                      <p className="mt-2 text-xs text-yellow-400">
                        기기 한도에 도달했습니다. 새 기기를 추가하려면 기존 기기를 제거하거나 업그레이드하세요.
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="mt-4 flex flex-wrap gap-3">
                    {currentLicense.tier !== 'team' && (
                      <button
                        onClick={() => navigate('/pricing')}
                        className="px-4 py-2 bg-space-blue text-white rounded-lg text-sm font-medium hover:bg-space-blue hover:brightness-110 transition-colors"
                      >
                        업그레이드
                      </button>
                    )}
                    {currentLicense.tier !== 'free' && (
                      <button
                        onClick={handleManageSubscription}
                        disabled={portalLoading}
                        className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50"
                      >
                        {portalLoading ? '로딩...' : '구독 관리'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Devices Card */}
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">등록된 기기</h2>
                <button
                  onClick={loadDevices}
                  disabled={isDevicesLoading}
                  className="text-sm text-space-blue-light hover:text-space-blue-light transition-colors disabled:opacity-50"
                >
                  {isDevicesLoading ? '로딩...' : '새로고침'}
                </button>
              </div>
              
              {isDevicesLoading ? (
                <div className="py-8 text-center">
                  <LoadingSpinner className="w-8 h-8 text-space-blue mx-auto" />
                </div>
              ) : devices.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  <DeviceIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>등록된 기기가 없습니다</p>
                  <p className="text-sm mt-1">moldClaw 앱에서 라이선스를 활성화하세요</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {devices.map((device) => (
                    <div
                      key={device.id}
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        device.is_current ? 'bg-space-blue/10 border border-space-blue/30' : 'bg-gray-900'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center">
                          <DeviceIcon className="w-5 h-5 text-gray-400" />
                        </div>
                        <div className="ml-3">
                          <div className="flex items-center">
                            <span className="font-medium text-white">{device.name}</span>
                            {device.is_current && (
                              <span className="ml-2 px-2 py-0.5 bg-space-blue/20 text-space-blue-light text-xs rounded">
                                현재 기기
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {device.platform} • 마지막 접속: {formatRelativeTime(device.last_seen_at)}
                          </div>
                        </div>
                      </div>
                      
                      {!device.is_current && (
                        <button
                          onClick={() => handleRemoveDevice(device.id, device.name)}
                          disabled={removeLoading === device.id}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                          title="기기 제거"
                        >
                          {removeLoading === device.id ? (
                            <LoadingSpinner className="w-5 h-5" />
                          ) : (
                            <TrashIcon className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Quick Tips */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-sm font-semibold text-gray-400 mb-3">💡 팁</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>• 기기를 교체하려면 기존 기기를 제거한 후 새 기기에서 라이선스를 활성화하세요.</li>
                <li>• 라이선스 코드를 분실한 경우 이 페이지에서 확인할 수 있습니다.</li>
                <li>• 결제 관련 문의는 "구독 관리"에서 처리할 수 있습니다.</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Helper Functions
// ============================================

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return '방금 전';
  if (diffMins < 60) return `${diffMins}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;
  if (diffDays < 7) return `${diffDays}일 전`;
  return date.toLocaleDateString('ko-KR');
}

// ============================================
// Icons
// ============================================

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function KeyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
    </svg>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" />
    </svg>
  );
}

function DeviceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}
